<script setup lang="ts">
import EnabledToolsItem from "@/components/ai-chat/EnabledToolsItem.vue";
import { useAssistantTools } from "@/composables/ai-chat/useAssistantTools";
import { AiAssistant } from "@/services/fobizzToolsApi";
import { AssistantTool } from "@/types/ai-chat.types";
import { nFormatter } from "@/utils/formatString";

const { t } = useI18n();

const userStore = useUserStore();
const user = userStore.user;

const { availableAssistantTools } = useAssistantTools(user);

type Props = {
  assistant: AiAssistant;
  open: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  open: false,
});

const emit = defineEmits(["createConversation", "remix", "hide"]);

const isSubmitting = ref(false);

const handleHide = () => {};

const handleHidden = () => {
  emit("hide");
};

const getModelNameTranslation = (modelId: string) => {
  if (!modelId) return "";
  const normalizedModelId = modelId.replace(".", "_");
  return t(`aiModelCards.${normalizedModelId}.name`);
};

const enabledTools = computed<AssistantTool[]>(() => {
  if (!props.assistant) return [];

  return props.assistant.enabledTools.map((id) => {
    return availableAssistantTools().find((app) => app.id === id);
  }) as AssistantTool[];
});

const startConversation = () => {
  isSubmitting.value = true;
  emit("createConversation", props.assistant);
};

const handleRemix = () => {
  emit("remix", props.assistant);
};

const handleSelect = (starter: string) => {
  emit("createConversation", props.assistant, starter);
};
</script>
<template>
  <teleport to="body">
    <BaseModal
      size="lg"
      :open="open"
      :scrollable="true"
      @hide="handleHide"
      @hidden="handleHidden"
    >
      <div class="modal-header">
        <button
          type="button"
          data-bs-dismiss="modal"
          class="btn-close"
          aria-label="Close"
        ></button>
      </div>
      <div class="modal-body pt-0 pb-5">
        <div class="modal-sections">
          <section class="modal-section">
            <div class="chat-assistant-intro">
              <ChatAssistantAvatar
                :assistant="assistant"
                size="lg"
                class="chat-assistant-intro__avatar"
              />
              <div>
                <h4
                  class="chat-assistant-intro__title"
                  data-testid="modal-title-ai_chat-assistant"
                >
                  {{ assistant.name }}
                </h4>
                <p class="chat-assistant-intro__text d-none d-lg-block">
                  {{ assistant.description }}
                </p>
              </div>
            </div>
            <p class="chat-assistant-intro__text d-lg-none mt-4 mb-1">
              {{ assistant.description }}
            </p>
          </section>
          <section class="modal-section">
            <div class="stat-columns">
              <div class="stat-columns__item">
                <div class="stat-columns__value">
                  {{ getModelNameTranslation(assistant.model || "") }}
                </div>
                <div class="stat-columns__label">
                  {{ t("aiChat.aiAssistant.labels.model") }}
                </div>
              </div>
              <div class="stat-columns__item">
                <div class="stat-columns__value">
                  {{ t(`aiCategories.categories.${assistant.category}`) }}
                </div>
                <div class="stat-columns__label">
                  {{ t("aiChat.labels.category") }}
                </div>
              </div>
              <div class="stat-columns__item">
                <div class="stat-columns__value">
                  {{ nFormatter(assistant.remixCount) }}
                </div>
                <div class="stat-columns__label">
                  {{ t("aiChat.aiAssistant.labels.remixCount") }}
                </div>
              </div>
              <div class="stat-columns__item">
                <div class="stat-columns__value">
                  {{ assistant.authorName || assistant.user.displayName }}
                </div>
                <div class="stat-columns__label">
                  {{ t("aiChat.aiAssistant.labels.author") }}
                </div>
              </div>
            </div>
          </section>
          <section v-if="enabledTools.length > 0" class="modal-section">
            <h5>
              {{ t("aiChat.aiAssistant.labels.enabledTools") }}
            </h5>
            <div class="enabled-tools-grid">
              <EnabledToolsItem
                v-for="app in enabledTools"
                :key="app.id"
                :app="app"
              />
            </div>
          </section>
          <section
            v-if="assistant.conversationStarters.length > 0"
            class="modal-section"
          >
            <h5>{{ t("aiChat.labels.conversationStarters") }}</h5>
            <ChatConversationStarters
              :starters="assistant.conversationStarters"
              @select="handleSelect"
            />
          </section>
        </div>
      </div>
      <div class="modal-footer">
        <div class="ms-auto button-grid">
          <button
            v-if="!userStore.isPupil"
            class="btn btn-secondary btn-rounded"
            @click="handleRemix"
          >
            <i class="bi bi-bootstrap-reboot me-1 hide-sm"></i>
            {{ t("links.remix") }}
          </button>
          <button
            class="btn btn-primary btn-rounded"
            data-testid="button-start-chat"
            :disabled="isSubmitting"
            @click="startConversation"
          >
            <BaseSpinner v-if="isSubmitting" size="sm" class="me-2" />
            {{ t("aiChat.links.startChat") }}
          </button>
        </div>
      </div>
    </BaseModal>
  </teleport>
</template>
<style lang="scss" scoped>
.chat-assistant-intro {
  display: flex;
  flex-direction: row;
  gap: $spacer-4;
  align-items: center;

  &__avatar {
    width: 140px;
    height: 140px;

    @include media-breakpoint-down(md) {
      width: 60px;
      height: 60px;
    }
  }

  &__title {
    @include media-breakpoint-down(md) {
      font-size: $font-size-sm;
    }
  }

  &__text {
    font-size: $font-size-sm;
    color: $secondary;
  }
}

.enabled-tools-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $spacer-2;

  @include media-breakpoint-down(md) {
    grid-template-columns: 1fr;
  }
}

.modal-sections {
  display: flex;
  flex-direction: column;
  gap: $spacer-6;

  @include media-breakpoint-down(md) {
    gap: $spacer-4;
  }
}

.modal-section {
  h5 {
    font-size: $font-size-sm;
    font-weight: $font-weight-semibold;
    margin-bottom: $spacer-2;
  }
}

.stat-columns {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-row-gap: $spacer;

  @include media-breakpoint-down(lg) {
    grid-template-columns: 1fr 1fr;
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: $spacer-1;
    padding: $spacer $spacer-2;
    background-color: $gray-100;
    text-align: center;
    position: relative;

    &::before {
      position: absolute;
      left: 0;
      top: 15%;
      height: 70%;
      width: 2px;
      background-color: $border-color;
      content: "";
    }

    &:first-child::before {
      width: 0;
    }

    @include media-breakpoint-down(lg) {
      &:nth-of-type(3)::before {
        width: 0;
      }
    }
  }

  &__value {
    font-weight: $font-weight-semibold;

    @include media-breakpoint-down(lg) {
      font-size: $font-size-sm;
    }
  }

  &__label {
    font-size: $font-size-xs;
    color: $secondary;
  }
}

.button-grid {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: $spacer-3;
}
</style>
